import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Hero } from "@/components/Hero";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { BrandMark } from "@/components/BrandMark";
import { ServicesSection } from "@/components/ServicesSection";
import { WhyChooseUs } from "@/components/WhyChooseUs";
import { ServiceAreasSection } from "@/components/ServiceAreasSection";
import { FAQSection } from "@/components/FAQSection";
import { LeadQuoteForm } from "@/components/LeadQuoteForm";
import { Reveal } from "@/components/Reveal";
import { FAQS, IMAGES } from "@/content/site";
import { ShortTermRentalSection } from "@/components/ShortTermRentalSection";
import { CommercialSection } from "@/components/CommercialSection";

export default function HomePage() {
  return (
    <>
      <SEO
        title="Pool Service Pinellas County FL | Point of Pines Pools & Service"
        description="Locally owned, CPO certified pool service across Pinellas County, FL. Weekly residential pool cleaning, vacation rental & commercial pool maintenance, equipment repairs and new pool startups. Call (727) 744-5908."
        path="/"
      />
      <Hero />
      <EditorialMarquee />
      <BrandMark />

      <section data-testid="home-intro-section" className="py-16 sm:py-24 lg:py-32 bg-shell">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <Reveal>
              <div className="relative">
                <div className="clip-arch overflow-hidden shadow-2xl shadow-navy/15 ring-1 ring-slate-200">
                  <img
                    src={IMAGES.ownerSkimming}
                    alt="Mike Strawn, owner of Point of Pines Pools & Service, skimming a residential pool in Pinellas County FL"
                    className="w-full h-96 sm:h-[30rem] object-cover"
                    loading="lazy"
                    data-testid="home-owner-photo"
                  />
                </div>
                <div
                  data-testid="home-owner-badge"
                  className="absolute -bottom-6 left-4 sm:left-8 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl shadow-navy/20 p-4 sm:p-5"
                >
                  <p className="font-display font-bold text-navy text-sm">That&apos;s me — Mike</p>
                  <p className="text-xs text-slate-500 mt-0.5">Owner &amp; Operator · CPO Certified · On your pool personally</p>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <Reveal>
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-pool font-semibold">A Different Kind of Pool Company</p>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] text-navy">
                Local care, professional standards, zero runaround
              </h2>
              <p className="mt-5 text-base text-slate-600 leading-relaxed">
                Point of Pines Pools &amp; Service is a locally owned and operated residential pool
                company serving all of Pinellas County — not a national franchise with a rotating
                cast of strangers. We specialize in dependable weekly pool maintenance, precise
                water chemistry, equipment service and repairs, and new pool surface startups.
              </p>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                From Clearwater to St. Petersburg, Largo to Palm Harbor, our customers get the same
                thing every week: a clean pool, balanced water, and a clear picture of what was done.
                And because we&apos;re owner-operated, the person in that photo is the person who
                shows up — and the person you can call.
              </p>
              <Link
                to="/about/"
                data-testid="home-about-link"
                className="group mt-7 inline-flex items-center gap-2 font-display font-bold text-pool hover:text-ocean transition-colors"
              >
                Learn more about us
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <ServicesSection
        intro="Residential pool care done thoroughly and documented clearly — every visit, every week."
      />
      <WhyChooseUs />
      <ShortTermRentalSection />
      <CommercialSection />
      <ServiceAreasSection compact />

      <FAQSection faqs={FAQS.slice(0, 5)} />

      <section data-testid="home-lead-form-section" className="py-16 sm:py-24 lg:py-32 bg-mist">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <Reveal>
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-pool font-semibold">Get Started</p>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] text-navy">
                Request pool service in about a minute
              </h2>
              <p className="mt-5 text-base text-slate-600 leading-relaxed">
                Tell us a little about your pool and what you need. We&apos;ll review it personally
                and get back to you with clear next steps — no pressure, no obligation.
              </p>
              <p className="mt-5 text-base text-slate-600">
                Prefer to talk? Call us at{" "}
                <a href="tel:+17277445908" data-testid="home-form-phone-link" className="font-semibold text-pool hover:text-ocean transition-colors">
                  (727) 744-5908
                </a>
                .
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <LeadQuoteForm />
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
